import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import { toast } from "sonner";
import {
  ArrowRight,
  Buildings,
  CheckCircle,
  CaretDown,
  ClockCounterClockwise,
  Flag,
  Phone,
  Lightbulb,
  Plus,
  TrendUp,
  UserCircle,
  Warning,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeGermanSellerRole(value) {
  if (typeof value !== "string") return value;

  return value
    .replace(/\bDer Commercial\b/g, "Der Vertriebsmitarbeiter")
    .replace(/\bder Commercial\b/g, "der Vertriebsmitarbeiter")
    .replace(/\bDen Commercial\b/g, "Den Vertriebsmitarbeiter")
    .replace(/\bden Commercial\b/g, "den Vertriebsmitarbeiter")
    .replace(/\bDem Commercial\b/g, "Dem Vertriebsmitarbeiter")
    .replace(/\bdem Commercial\b/g, "dem Vertriebsmitarbeiter")
    .replace(/\bEin Commercial\b/g, "Ein Vertriebsmitarbeiter")
    .replace(/\bein Commercial\b/g, "ein Vertriebsmitarbeiter")
    .replace(/\bEinen Commercial\b/g, "Einen Vertriebsmitarbeiter")
    .replace(/\beinen Commercial\b/g, "einen Vertriebsmitarbeiter");
}

function actionText(value) {
  if (typeof value !== "string") return value;
  const cleaned = normalizeGermanSellerRole(value)
    .replace(/^(?:(?:the|il|le|el|der)\s+)?(?:sales rep|commerciale|commercial|representante comercial|vertriebsmitarbeiter)\s*:\s*/i, "")
    .replace(/^(?:(?:the|il|le|el|der)\s+)?(?:sales rep|commerciale|commercial|representante comercial|vertriebsmitarbeiter)\s+(?:must|should|will|needs to|committed to|deve|dovrà|dovrebbe|si è impegnato a|doit|devra|s'est engagé à|debe|deberá|se comprometió a|muss|soll|wird)\s+/i, "")
    .trim();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : cleaned;
}

function itemText(item) {
  if (item == null) return "";
  if (typeof item === "string") return actionText(item);
  if (typeof item !== "object") return String(item);
  if (item.what) return actionText(item.what);
  if (item.text) return actionText(item.text);
  if (item.name) {
    return normalizeGermanSellerRole(`${item.name}${item.role ? ` · ${item.role}` : ""}`);
  }
  return normalizeGermanSellerRole(item.answer || item.description || "");
}

function meaningfulItemText(item) {
  const text = String(itemText(item) || "").trim();
  if (!text) return "";
  if (/^(?:not (?:mentioned|evident|established|available)|none identified|n\/a|—)$/i.test(text)) {
    return "";
  }
  return text;
}

function memoryTokens(value) {
  const stopWords = new Set([
    "alla", "allo", "anche", "come", "dalla", "delle", "della", "degli", "dello",
    "into", "from", "that", "the", "this", "with", "pour", "dans", "avec", "para", "con",
  ]);
  return new Set(
    itemText(value)
      .toLocaleLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .match(/[a-z0-9]+/g)
      ?.filter((token) => token.length >= 4 && !stopWords.has(token)) || []
  );
}

function memoryOverlap(left, right) {
  const a = memoryTokens(left);
  const b = memoryTokens(right);
  if (!a.size || !b.size) return false;
  let shared = 0;
  a.forEach((token) => {
    if (b.has(token)) shared += 1;
  });
  return shared / Math.min(a.size, b.size) >= 0.68;
}

function uniqueMemoryItems(items, exclude = [], limit = 8) {
  const kept = [];
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!meaningfulItemText(item)) return;
    if (exclude.some((other) => memoryOverlap(item, other))) return;
    if (kept.some((other) => memoryOverlap(item, other))) return;
    if (kept.length < limit) kept.push(item);
  });
  return kept;
}

function GroupedMemoryCard({ title, groups, tone = "violet" }) {
  const visibleGroups = groups
    .map((group) => ({ ...group, items: uniqueMemoryItems(group.items, [], 6) }))
    .filter((group) => group.items.length);
  if (!visibleGroups.length) return null;
  return (
    <div className={`relationship-memory-group-card relationship-memory-group-card--${tone}`}>
      <div className="relationship-memory-list__title">{title}</div>
      <div className="relationship-memory-group-card__groups">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <small>{group.label}</small>
            <ul>
              {group.items.map((item, index) => (
                <li key={index}>
                  <span aria-hidden="true">•</span>
                  <span>{itemText(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function memorySourceCallId(item, fallback = null) {
  if (!item || typeof item !== "object") return fallback;
  return item.source_call_id || item.last_seen_call_id || item.call_id || fallback;
}

function LastObservedLink({
  callId,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
  label = "Last observed",
}) {
  if (!callId) return null;
  const source = sourceMap?.[String(callId)] || {};

  const deepLinkSearch = deepDiveTarget
    ? `?deepDiveTarget=${encodeURIComponent(deepDiveTarget)}`
    : "";

  return (
    <Link
      to={{ pathname: `/calls/${callId}`, search: deepLinkSearch }}
      state={{
        from: "relationship",
        relationshipKey,
        relationshipName,
        ...(deepDiveTarget ? { deepDiveTarget } : {}),
      }}
      className="relationship-source-link"
      title={source.title ? `Open ${source.title}` : "Open supporting call"}
    >
      <span>{label}</span>
      <strong>{source.title || "Supporting call"}</strong>
      {source.date && <small>{fmtDate(source.date)}</small>}
      <ArrowRight size={12} weight="bold" aria-hidden="true" />
    </Link>
  );
}

function MemoryList({
  title,
  items,
  tone = "violet",
  card = false,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
}) {
  const visible = Array.isArray(items) ? items.filter(meaningfulItemText).slice(0, 6) : [];
  if (!visible.length) return null;

  return (
    <div
      className={`relationship-memory-list relationship-memory-list--${tone}${
        card ? " relationship-memory-list--card" : ""
      }`}
    >
      <div className="relationship-memory-list__title">{title}</div>
      <ul>
        {visible.map((item, index) => {
          const callId = memorySourceCallId(item);
          return (
            <li key={index}>
              <span aria-hidden="true">•</span>
              <span className="relationship-memory-list__item-copy">
                <span>{itemText(item)}</span>
                <LastObservedLink
                  callId={callId}
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget={deepDiveTarget}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function relationshipScoreTone(value, invert = false) {
  const normalized = Math.max(0, Math.min(100, Number(value) || 0));
  const interpreted = invert ? 100 - normalized : normalized;

  if (interpreted >= 85) return "excellent";
  if (interpreted >= 70) return "good";
  if (interpreted >= 55) return "caution";
  if (interpreted >= 40) return "warning";
  return "danger";
}

function StateMetric({
  label,
  value,
  score,
  tone = "default",
  invert = false,
  className = "",
  sourceCallId,
  sourceMap,
  relationshipKey,
  relationshipName,
  deepDiveTarget,
  sourceLabel = "Last observed",
}) {
  const hasScore = score !== undefined && score !== null;
  const shown = value || (hasScore ? `${score}` : "—");
  const metricClass = hasScore
    ? `relationship-state-metric--score relationship-state-metric--score-${relationshipScoreTone(score, invert)}`
    : `relationship-state-metric--${tone}`;

  return (
    <div
      className={`relationship-state-metric ${metricClass} ${className}`.trim()}
    >
      <div className="relationship-state-metric__label">{label}</div>
      <div className="relationship-state-metric__value">{shown}</div>
      <LastObservedLink
        callId={sourceCallId}
        sourceMap={sourceMap}
        relationshipKey={relationshipKey}
        relationshipName={relationshipName}
        deepDiveTarget={deepDiveTarget}
        label={sourceLabel}
      />
    </div>
  );
}

function evolutionStep(step) {
  if (typeof step === "string") {
    return { text: normalizeGermanSellerRole(step), status: "current" };
  }

  return {
    text: normalizeGermanSellerRole(step?.text || ""),
    status: step?.status || "current",
  };
}

const EVOLUTION_COLOR = {
  resolved: "is-resolved",
  recurring: "is-recurring",
  new: "is-new",
  open: "is-open",
  current: "is-current",
};

function RelationshipActionCard({ owner, action }) {
  if (!action || !meaningfulItemText(action)) return null;

  const ownerKey = owner === "customer" ? "customer" : "sales_rep";
  const ownerLabel = ownerKey === "customer" ? "Client" : "Rep";
  const deadline = String(action?.deadline || "").trim();
  const showDeadline = deadline && !/^(?:not established|unknown|none|n\/a)$/i.test(deadline);

  return (
    <article className={`next-call-action next-call-action--${ownerKey}`}>
      <span className={`next-call-action__owner next-call-action__owner--${ownerKey}`}>
        {ownerLabel}
      </span>
      <div className="next-call-action__copy">
        <strong>{itemText(action)}</strong>
        {showDeadline && <small>{deadline}</small>}
      </div>
    </article>
  );
}

function RelationshipMemory({ memory, onRebuild, rebuilding, milestones, relationshipKey, relationshipName }) {
  if (!memory) return null;

  if (!memory.available || memory.needs_rebuild) {
    return (
      <section id="next-call" className="relationship-memory-empty" data-testid="memory-empty-state">
        <div className="relationship-memory-empty__identity">
          <span className="relationship-memory-empty__icon">
            <ClockCounterClockwise size={22} weight="duotone" />
          </span>
          <div>
            <h2>{memory.needs_rebuild ? "Memory needs a refresh" : "Build the relationship memory"}</h2>
            <p>
              {memory.needs_rebuild
                ? "The memory schema changed. Rebuild the compact state from the stored calls."
                : "Build a compact longitudinal state from the analyses already stored for this customer."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRebuild}
          disabled={rebuilding}
          className="relationship-action-button"
          data-testid="build-relationship-memory"
        >
          <ClockCounterClockwise size={15} weight="bold" />
          {rebuilding ? "Building memory..." : memory.needs_rebuild ? "Rebuild memory" : "Build relationship memory"}
        </button>
      </section>
    );
  }

  const state = memory.current_state || {};
  const facts = state.facts || {};
  const inferences = state.inferences || {};
  const brief = memory.next_call_brief || {};

  const commitments = uniqueMemoryItems(
    (facts.commitments || []).filter((item) => item?.status === "open" || item?.status === "unknown"),
  );

  const customerAction = commitments.find((item) => (
    String(item?.owner || "").toLowerCase() === "customer"
  ));
  const repAction = commitments.find((item) => (
    ["sales_rep", "rep", "am"].includes(String(item?.owner || "").toLowerCase())
  ));

  const commercialObjections = uniqueMemoryItems(
    (facts.commercial_objections || facts.explicit_objections || []).filter(
      (item) => !item?.status || item.status === "open",
    ),
  );
  const blockers = uniqueMemoryItems(
    (facts.operational_blockers || []).filter((item) => !item?.status || item.status === "open"),
    commercialObjections,
  );
  const preferences = uniqueMemoryItems(
    (facts.process_preferences || []).filter((item) => !item?.status || item.status === "active"),
    [...commercialObjections, ...blockers],
  );
  const riskSource = Array.isArray(brief.risks) && brief.risks.length
    ? brief.risks
    : inferences.risks;
  const opportunitySource = Array.isArray(brief.opportunities) && brief.opportunities.length
    ? brief.opportunities
    : inferences.opportunities;

  const risks = uniqueMemoryItems(
    riskSource,
    [...commercialObjections, ...blockers, ...preferences, ...commitments],
    6,
  );
  const opportunities = uniqueMemoryItems(
    opportunitySource,
    [...commercialObjections, ...blockers, ...preferences, ...risks, ...commitments],
    6,
  );

  const sourceMap = Object.fromEntries(
    (Array.isArray(milestones) ? milestones : []).map((milestone) => [
      String(milestone.call_id),
      milestone,
    ])
  );
  const statusSourceCallId = state.as_of_call_id || memory.last_processed_call_id;
  const hasNextActions = Boolean(customerAction || repAction);
  const hasWatchItems = Boolean(
    commercialObjections.length || blockers.length || risks.length || opportunities.length
  );
  const stakeholders = uniqueMemoryItems(facts.stakeholders, [], 6).filter((item) => (
    !/^(?:non nominato|unnamed|unknown)\b/i.test(itemText(item))
  ));

  return (
    <section className="relationship-memory relationship-memory--final" data-testid="relationship-memory">
      <article id="next-call" className="next-call-brief next-call-brief--actions">
        <div className="relationship-card-header">
          <div><h2>Before the next call</h2></div>
          <div className="relationship-memory__header-actions">
            <span className="relationship-card-kicker">Live memory</span>
            <button
              type="button"
              className="relationship-memory__refresh"
              onClick={() => {
                const confirmed = window.confirm(
                  "Rebuild the relationship memory from all stored calls? This will replace the current compact state."
                );
                if (confirmed) onRebuild();
              }}
              disabled={rebuilding}
              data-testid="refresh-relationship-memory"
              title="Rebuild from all stored calls"
            >
              <ClockCounterClockwise size={14} weight="bold" aria-hidden="true" />
              {rebuilding ? "Refreshing..." : "Refresh memory"}
            </button>
          </div>
        </div>

        {hasNextActions ? (
          <div className="next-call-actions">
            <RelationshipActionCard owner="customer" action={customerAction} />
            <RelationshipActionCard owner="sales_rep" action={repAction} />
          </div>
        ) : (
          <p className="next-call-actions__empty">No explicit customer or rep action is currently open.</p>
        )}
      </article>

      <article id="relationship-state" className="relationship-state relationship-state--final">
        <div className="relationship-card-header">
          <div><h2>Current relationship state</h2></div>
        </div>

        <div className="relationship-state__snapshot relationship-state__snapshot--final">
          <div className="relationship-state__snapshot-copy relationship-state__snapshot-copy--two">
            <MemoryList
              title="Stakeholders"
              items={stakeholders}
              tone="violet"
              card
              sourceMap={sourceMap}
              relationshipKey={relationshipKey}
              relationshipName={relationshipName}
            />
            <StateMetric
              label="Status Quo"
              value={facts.customer_position}
              className="relationship-state-metric--summary"
              sourceCallId={statusSourceCallId}
              sourceMap={sourceMap}
              relationshipKey={relationshipKey}
              relationshipName={relationshipName}
              sourceLabel="Last updated"
            />
          </div>

          <div className="relationship-state__score-grid relationship-state__score-grid--three" aria-label="Commercial signals">
            <StateMetric label="Buying intent" score={inferences.buying_intent?.score} />
            <StateMetric label="Churn risk" score={inferences.churn_risk?.score} invert />
            <StateMetric label="Trust" score={inferences.trust?.score} />
          </div>
        </div>

        <div className="relationship-state__details relationship-state__details--final">
          {hasWatchItems && (
            <div className="relationship-state__section">
              <div className="relationship-state__section-title">What to watch</div>
              <div className="relationship-state__watch-grid">
                <MemoryList
                  title="Blockers"
                  items={blockers}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="blockers"
                />
                <MemoryList
                  title="Objections"
                  items={commercialObjections}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="objections"
                />
                <MemoryList
                  title="Risks"
                  items={risks}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="risks"
                />
                <MemoryList
                  title="Opportunities"
                  items={opportunities}
                  tone="violet"
                  card
                  sourceMap={sourceMap}
                  relationshipKey={relationshipKey}
                  relationshipName={relationshipName}
                  deepDiveTarget="opportunities"
                />
              </div>
            </div>
          )}

          <div className="relationship-state__section">
            <div className="relationship-state__section-title">Human dynamics</div>
            <div className="relationship-state__health-grid">
              <StateMetric label="Relationship score" score={inferences.relationship_strength?.score} />
              <StateMetric
                label="Stance"
                value={inferences.attitude?.text}
                sourceCallId={memorySourceCallId(inferences.attitude, statusSourceCallId)}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                deepDiveTarget="stance"
              />
              <StateMetric
                label="Tone"
                value={inferences.emotional_tone?.text}
                sourceCallId={memorySourceCallId(inferences.emotional_tone, statusSourceCallId)}
                sourceMap={sourceMap}
                relationshipKey={relationshipKey}
                relationshipName={relationshipName}
                deepDiveTarget="tone"
              />
            </div>
          </div>
        </div>
      </article>
    </section>
  );
}

const OUTCOME_CLASS = {
  "Churn risk": "is-risk",
  "Ready to move": "is-positive",
  "Closing": "is-positive",
  "Stalled": "is-warning",
  "Cool": "is-neutral",
  "In progress": "is-progress",
  "Recorded": "is-neutral",
};

function Chip({ label, tone = "is-neutral", testId }) {
  return (
    <span data-testid={testId} className={`relationship-chip ${tone}`}>
      {label}
    </span>
  );
}

function Metric({ label, val, bar, danger }) {
  const shown = normalizeGermanSellerRole(val) || "—";

  return (
    <div className="timeline-metric">
      <div className="timeline-metric__label">{label}</div>
      <div className="timeline-metric__value" title={shown}>{shown}</div>
      {bar !== undefined && bar > 0 && (
        <div className="timeline-metric__track">
          <div
            className={`timeline-metric__fill${danger ? " is-danger" : ""}`}
            style={{ width: `${Math.min(100, bar)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function TimelineCard({ milestone, index, isLast, relationshipKey, relationshipName, isActive }) {
  const {
    call_id,
    title,
    date,
    customer_mood,
    main_objection,
    turning_point,
    next_step,
    negotiation_stage,
    confidence,
    churn_risk,
    outcome,
    relationship_update,
  } = milestone;

  const outcomeTone = OUTCOME_CLASS[outcome] || OUTCOME_CLASS.Recorded;
  const changeSummary = relationship_update?.changes?.summary || [];
  const changeLabel = relationship_update?.previous_call_id
    ? "Since previous call"
    : "Initial relationship state";

  return (
    <div
      id={`timeline-call-${call_id}`}
      className={`relationship-timeline-item${isActive ? " is-active" : ""}`}
      data-testid={`timeline-card-${index}`}
    >
      <div className={`relationship-timeline-rail${isLast ? " is-last" : ""}`} aria-hidden="true">
        <span />
      </div>

      <Link
        to={`/calls/${call_id}`}
        state={{ from: "relationship", relationshipKey, relationshipName }}
        className="relationship-timeline-card"
        aria-label={`Open ${title || "call"}`}
      >
        <span className="relationship-timeline-card__top">
          <span>
            <small>{fmtDate(date)}</small>
            <strong title={title || "Untitled call"}>{title || "Untitled call"}</strong>
          </span>

          <span className="relationship-timeline-card__chips">
            <Chip label={outcome} tone={outcomeTone} testId={`chip-outcome-${index}`} />
            {negotiation_stage && (
              <Chip label={negotiation_stage} tone="is-stage" testId={`chip-stage-${index}`} />
            )}
          </span>
        </span>

        <span className="relationship-timeline-card__metrics">
          <Metric label="Tone" val={customer_mood} />
          <Metric label="Buying intent" val={confidence ? `${confidence}` : "—"} bar={confidence} />
          <Metric label="Churn risk" val={churn_risk ? `${churn_risk}` : "—"} bar={churn_risk} danger />
          <Metric label="Objection" val={main_objection || "—"} />
        </span>

        {(turning_point || next_step || changeSummary.length > 0) && (
          <span className="relationship-timeline-card__insights">
            {turning_point && (
              <span className="timeline-insight">
                <span className="timeline-insight__icon is-violet">
                  <TrendUp size={15} weight="bold" />
                </span>
                <span>
                  <small>Turning point</small>
                  <span>{normalizeGermanSellerRole(turning_point)}</span>
                </span>
              </span>
            )}

            {next_step && (
              <span className="timeline-insight">
                <span className="timeline-insight__icon is-emerald">
                  <CheckCircle size={15} weight="fill" />
                </span>
                <span>
                  <small>Next step</small>
                  <span>{actionText(next_step)}</span>
                </span>
              </span>
            )}

            {changeSummary.length > 0 && (
              <span className="timeline-insight timeline-insight--wide">
                <span className="timeline-insight__icon is-cyan">
                  <ClockCounterClockwise size={15} weight="bold" />
                </span>
                <span>
                  <small>{changeLabel}</small>
                  <span>{changeSummary.slice(0, 3).map(itemText).filter(Boolean).join(" · ")}</span>
                </span>
              </span>
            )}
          </span>
        )}

        <span className="relationship-timeline-card__open">
          Open call
          <ArrowRight size={14} weight="bold" />
        </span>
      </Link>
    </div>
  );
}

function ConversationRow({ milestone, index, relationshipKey, relationshipName, isActive }) {
  const {
    call_id,
    title,
    date,
    customer_mood,
    negotiation_stage,
    outcome,
  } = milestone;
  const outcomeTone = OUTCOME_CLASS[outcome] || OUTCOME_CLASS.Recorded;

  return (
    <Link
      id={`timeline-call-${call_id}`}
      to={`/calls/${call_id}`}
      state={{ from: "relationship", relationshipKey, relationshipName }}
      className={`relationship-conversation-row${isActive ? " is-active" : ""}`}
      aria-label={`Open ${title || "call"}`}
      data-testid={`conversation-row-${index}`}
    >
      <span className="relationship-conversation-row__icon" aria-hidden="true">
        <Phone size={17} weight="duotone" />
      </span>

      <span className="relationship-conversation-row__copy">
        <strong title={title || "Untitled call"}>{title || "Untitled call"}</strong>
        <small>
          {fmtDate(date)}
          {customer_mood ? ` · ${customer_mood}` : ""}
        </small>
      </span>

      <span className="relationship-conversation-row__chips">
        {outcome && <Chip label={outcome} tone={outcomeTone} />}
        {negotiation_stage && <Chip label={negotiation_stage} tone="is-stage" />}
      </span>

      <ArrowRight
        size={15}
        weight="bold"
        className="relationship-conversation-row__arrow"
        aria-hidden="true"
      />
    </Link>
  );
}

export default function CustomerTimelinePage() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [showAllCalls, setShowAllCalls] = useState(false);
  const nav = useNavigate();
  const location = useLocation();
  const highlightedCallRef = useRef(false);
  const activeCallId = location.state?.activeCallId;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/customers/${encodeURIComponent(key)}/timeline`);
        setData(data);
      } catch {
        toast.error("Failed to load timeline");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!data || !activeCallId) return;
    const sortedMilestones = [...(data.milestones || [])].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const activeIndex = sortedMilestones.findIndex(
      (milestone) => String(milestone.call_id) === String(activeCallId)
    );
    if (activeIndex >= 3) setShowAllCalls(true);
  }, [activeCallId, data]);

  useEffect(() => {
    if (!data || !activeCallId || highlightedCallRef.current) return;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(`timeline-call-${activeCallId}`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        highlightedCallRef.current = true;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [activeCallId, data, showAllCalls]);

  const rebuildMemory = async () => {
    const relationshipId = data?.customer?.id || key;
    setRebuilding(true);

    try {
      const result = await api.post(`/customers/${encodeURIComponent(relationshipId)}/memory/rebuild`);
      const refreshed = await api.get(`/customers/${encodeURIComponent(relationshipId)}/timeline`);
      setData(refreshed.data);
      toast.success(`Relationship memory built from ${result.data.processed_calls} calls`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to build relationship memory");
    } finally {
      setRebuilding(false);
    }
  };

  if (loading) {
    return (
      <div className="relationship-page">
        <Nav />
        <main className="relationship-shell">
          <div className="relationship-page-loading">
            <div className="relationship-page-loading__hero" />
            <div className="relationship-page-loading__panel" />
            <div className="relationship-page-loading__panel" />
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="relationship-page">
        <Nav />
        <main className="relationship-shell">
          <div className="relationship-not-found">
            <Warning size={24} weight="duotone" />
            <strong>Relationship not found</strong>
            <button type="button" onClick={() => nav("/customers")}>Back to customers</button>
          </div>
        </main>
      </div>
    );
  }

  const { customer, milestones, objection_evolution, relationship_memory } = data;
  const newestFirstMilestones = [...milestones].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  const isFirstCall = newestFirstMilestones.length <= 1;
  const compactMilestones = newestFirstMilestones.slice(0, 3);
  const historyButtonLabel = showAllCalls
    ? "Compact history"
    : newestFirstMilestones.length > 3
      ? `View all ${newestFirstMilestones.length} calls`
      : "View call details";

  return (
    <div className="relationship-page">
      <Nav />

      <main className="relationship-shell">
        <div className="relationship-topbar">
          <AppBreadcrumbs
            items={[
              { label: "Customers", to: "/customers" },
              { label: customer.name },
            ]}
          />

          <Link to="/upload" className="relationship-upload-button">
            <Plus size={14} weight="bold" />
            Upload another call
          </Link>
        </div>

        <section className="relationship-hero">
          <div className="relationship-hero__content">
            <span className="relationship-hero__avatar">
              <UserCircle size={31} weight="duotone" />
            </span>

            <div className="relationship-hero__copy">
              <h1>{customer.name}</h1>
              <div className="relationship-hero__meta">
                {customer.company && (
                  <span>
                    <Buildings size={14} />
                    {customer.company}
                  </span>
                )}
                <span>
                  <Phone size={14} />
                  {customer.call_count} call{customer.call_count === 1 ? "" : "s"}
                </span>
                <span>
                  <ClockCounterClockwise size={14} />
                  {fmtDate(customer.first_call_at)} → {fmtDate(customer.last_call_at)}
                </span>
              </div>
            </div>
          </div>
        </section>

        <nav className="relationship-section-nav" aria-label="Relationship sections">
          <a href="#conversation-history">
            <Phone size={15} weight="duotone" />
            Conversation history
            <span>{newestFirstMilestones.length}</span>
          </a>
          <a href="#next-call">
            <CheckCircle size={15} weight="duotone" />
            Next call
          </a>
          <a href="#relationship-state">
            <UserCircle size={15} weight="duotone" />
            Relationship state
          </a>
          {objection_evolution && objection_evolution.length > 0 && (
            <a href="#objections">
              <Flag size={15} weight="duotone" />
              Objections
            </a>
          )}
        </nav>

        <section
          id="conversation-history"
          className="relationship-history relationship-history--priority"
        >
          <div className="relationship-history__header">
            <div>
              <h2>Conversation history</h2>
            </div>

            <div className="relationship-history__actions">
              <span>
                {newestFirstMilestones.length} call{newestFirstMilestones.length === 1 ? "" : "s"}
              </span>
              {newestFirstMilestones.length > 0 && (
                <button
                  type="button"
                  className="relationship-history__toggle"
                  onClick={() => setShowAllCalls((current) => !current)}
                  aria-expanded={showAllCalls}
                >
                  {historyButtonLabel}
                  <CaretDown size={14} weight="bold" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {showAllCalls ? (
            <div className="relationship-history__list">
              {newestFirstMilestones.map((milestone, index) => (
                <TimelineCard
                  key={milestone.call_id}
                  milestone={milestone}
                  index={index}
                  isLast={index === newestFirstMilestones.length - 1}
                  relationshipKey={key}
                  relationshipName={customer.name}
                  isActive={String(milestone.call_id) === String(activeCallId || "")}
                />
              ))}
            </div>
          ) : (
            <div className="relationship-conversation-list">
              {compactMilestones.map((milestone, index) => (
                <ConversationRow
                  key={milestone.call_id}
                  milestone={milestone}
                  index={index}
                  relationshipKey={key}
                  relationshipName={customer.name}
                  isActive={String(milestone.call_id) === String(activeCallId || "")}
                />
              ))}
            </div>
          )}

          <aside
            className="relationship-memory-tip relationship-memory-tip--embedded"
            data-testid="relationship-memory-tip"
          >
            <span className="relationship-memory-tip__icon" aria-hidden="true">
              <Lightbulb size={19} weight="duotone" />
            </span>
            <div className="relationship-memory-tip__copy">
              <strong>
                {isFirstCall
                  ? "Build this relationship over time"
                  : "Keep this relationship memory growing"}
              </strong>
              <p>
                {isFirstCall
                  ? "Upload future calls using the same customer or company name. Callsect will connect them automatically and build a continuous relationship memory."
                  : "Keep uploading future calls using the same customer or company name. Callsect will extend the timeline and keep the relationship memory up to date."}
              </p>
            </div>
            <Link
              to="/upload"
              className="relationship-memory-tip__action"
              aria-label="Upload another call"
            >
              <ArrowRight size={16} weight="bold" />
            </Link>
          </aside>
        </section>

        <RelationshipMemory
          memory={relationship_memory}
          onRebuild={rebuildMemory}
          rebuilding={rebuilding}
          milestones={newestFirstMilestones}
          relationshipKey={key}
          relationshipName={customer.name}
        />

        {objection_evolution && objection_evolution.length > 0 && (
          <section id="objections" className="objection-evolution" data-testid="evolution-chain">
            <div className="relationship-card-header">
              <div>
                <h2>Objection evolution</h2>
              </div>
            </div>

            <div className="objection-evolution__chain">
              {objection_evolution.map((rawStep, index) => {
                const step = evolutionStep(rawStep);

                return (
                  <div key={index} className="objection-evolution__step">
                    <span
                      data-testid={`evolution-step-${index}`}
                      className={`objection-evolution__chip ${EVOLUTION_COLOR[step.status] || EVOLUTION_COLOR.current}`}
                    >
                      <Flag size={13} weight="fill" />
                      <span className="objection-evolution__text">{step.text}</span>
                    </span>

                    {index < objection_evolution.length - 1 && (
                      <ArrowRight size={14} className="objection-evolution__arrow" />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
